/* ══════════════════════════════════════════════════════════════════════════
   Modulo: Bundle Builder (bb)

   The library's first multi-item cart submission: every other section posts
   exactly one line. The hero plus each selected add-on go in ONE /cart/add.js
   call, each tagged with a shared bundle id and a role, then the shared
   cart-sync engine tells the theme.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  /* A LIVE query, never a load-time snapshot — a shopper can toggle the OS
     setting mid-session and the page must follow (the `as` lesson). */
  var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(RM && RM.matches); }

  /* ── Money ────────────────────────────────────────────────────────────────
     The total is a sum of an arbitrary subset, so Liquid cannot pre-render it
     and JS must format. Shopify has five amount tokens and they differ in BOTH
     separators, not one: amount_with_comma_separator is 1.234,56 (period
     thousands, COMMA decimal). Getting that wrong renders every SEK/EUR store
     incorrectly — the competitor ships `'$' + (cents/100).toFixed(2)`. */
  function moneyFormatter(fmt) {
    var pattern = fmt && fmt.length ? fmt : '${{amount}}';
    function group(cents, dec, thousands, decimal) {
      var s = (Math.abs(cents) / 100).toFixed(dec);
      var parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (cents < 0 ? '-' : '') + (parts[1] ? parts[0] + decimal + parts[1] : parts[0]);
    }
    return function (cents) {
      return String(pattern).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, tok) {
        switch (tok) {
          case 'amount':                                  return group(cents, 2, ',', '.');
          case 'amount_no_decimals':                      return group(cents, 0, ',', '.');
          case 'amount_with_comma_separator':             return group(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator':        return group(cents, 2, "'", '.');
          default:                                        return group(cents, 2, ',', '.');
        }
      });
    };
  }

  function initSection(root) {
    if (root.moduloBb) return;

    var min      = parseInt(root.getAttribute('data-min') || '0', 10);
    var max      = parseInt(root.getAttribute('data-max') || '1', 10);
    var discount = parseFloat(root.getAttribute('data-discount') || '0') || 0;
    var buyable  = root.getAttribute('data-buyable') === '1';
    var money    = moneyFormatter(root.getAttribute('data-money-format'));

    var cards    = [].slice.call(root.querySelectorAll('[data-bb-card]'));
    var spine    = root.querySelector('[data-bb-spine]');
    var countEl  = root.querySelector('[data-bb-count]');
    var bar      = root.querySelector('[data-bb-bar]');
    var bubble   = root.querySelector('[data-bb-bubble]');
    var barCount = root.querySelector('[data-bb-barcount]');
    var totalEl  = root.querySelector('[data-bb-total]');
    var wasEl    = root.querySelector('[data-bb-was]');
    var saveEl   = root.querySelector('[data-bb-save]');
    var submit   = root.querySelector('[data-bb-submit]');
    var label    = root.querySelector('[data-bb-submit-label]');
    var live     = root.querySelector('[data-bb-live]');

    /* The hero's price is server-rendered; read it off the markup rather than
       trusting a second data attribute to agree with what is printed. */
    var heroPrice = parseInt(root.getAttribute('data-hero-price') || '0', 10) || 0;
    var heroId    = root.getAttribute('data-hero-variant') || '';

    var selected = [];      /* card elements, in the order they were chosen */
    var lastLit  = 0;
    var lastN    = null;
    var inflight = false;

    function priceOf(card) {
      var sel = card.parentNode ? card.parentNode.querySelector('[data-bb-variants]') : null;
      if (sel && sel.options.length) {
        var o = sel.options[sel.selectedIndex];
        if (o) return parseInt(o.getAttribute('data-price') || '0', 10) || 0;
      }
      return parseInt(card.getAttribute('data-price') || '0', 10) || 0;
    }
    function variantOf(card) {
      var sel = card.parentNode ? card.parentNode.querySelector('[data-bb-variants]') : null;
      if (sel && sel.value) return sel.value;
      return card.getAttribute('data-variant') || '';
    }

    function bump(el) {
      if (!el || reduced()) return;
      /* Remove, force a reflow, re-add — without it a fast second tap lands
         inside the running animation and gets no feedback at all. */
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    }

    function render() {
      var n = selected.length;
      var full = n >= max;

      /* Notches: only the one that just lit pops, so deselecting does not
         replay the whole rail. */
      if (spine) {
        var notches = spine.children;
        for (var i = 0; i < notches.length; i++) {
          var on = i < n;
          notches[i].className = 'modulo-bb__notch' +
            (on ? ' modulo-bb__notch--on' : '') +
            (on && i === n - 1 && n > lastLit && !reduced() ? ' modulo-bb__notch--pop' : '');
        }
      }
      lastLit = n;

      for (var c = 0; c < cards.length; c++) {
        var card = cards[c];
        var isOn = selected.indexOf(card) !== -1;
        card.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        /* Selected cards stay live so a slot can always be freed. A card with no
           variant (the design-mode demo) is never selectable. */
        if (!card.hasAttribute('data-variant')) continue;
        card.disabled = !isOn && full;
      }

      var raw = heroPrice;
      for (var s = 0; s < selected.length; s++) raw += priceOf(selected[s]);

      /* The saving is a promise about checkout, and it only exists once there is
         something bundled. Never a bare strikethrough on the hero alone. */
      var live_discount = discount > 0 && n > 0;
      var due = live_discount ? Math.round(raw * (1 - discount / 100)) : raw;

      var items = n + 1;
      var changed = lastN !== null && lastN !== items;
      lastN = items;

      if (bubble) bubble.textContent = String(items);
      if (barCount) {
        barCount.textContent = n === 0
          ? '1 item in your bundle'
          : items + ' items in your bundle';
      }
      if (totalEl) totalEl.textContent = money(due);
      if (wasEl) {
        wasEl.hidden = !live_discount;
        if (live_discount) wasEl.textContent = money(raw);
      }
      if (saveEl) {
        saveEl.hidden = !live_discount;
        if (live_discount) saveEl.textContent = discount + '% off at checkout';
      }
      if (changed) { bump(totalEl); bump(bubble); }

      if (countEl) {
        countEl.textContent = full
          ? max + ' of ' + max + ' added — remove one to swap'
          : n + ' of ' + max + ' added';
      }

      if (submit && label && !inflight) {
        var need = min - n;
        if (!buyable) {
          submit.disabled = true;
        } else if (need > 0) {
          submit.disabled = true;
          label.textContent = 'Add ' + need + (need === 1 ? ' more item' : ' more items');
        } else {
          submit.disabled = false;
          label.textContent = submit.getAttribute('data-label-default') || label.textContent;
        }
      }

      if (live) {
        live.textContent = items + ' items, ' + money(due) +
          (min - n > 0 ? ', ' + (min - n) + ' more needed' : '');
      }
    }

    /* ── Selection ───────────────────────────────────────────────────────── */
    for (var k = 0; k < cards.length; k++) {
      (function (card) {
        if (!card.hasAttribute('data-variant')) return;   /* demo card */
        card.addEventListener('click', function () {
          var at = selected.indexOf(card);
          if (at !== -1) selected.splice(at, 1);
          else if (selected.length < max) selected.push(card);
          render();
        });
        var sel = card.parentNode ? card.parentNode.querySelector('[data-bb-variants]') : null;
        if (sel) {
          sel.addEventListener('change', function () {
            var o = sel.options[sel.selectedIndex];
            var priceEl = card.querySelector('[data-bb-price]');
            /* JS NEVER formats a variant price — Liquid pre-rendered it, because
               a theme's own formatMoney is hostile (Ella throws one-arg). */
            if (priceEl && o) priceEl.textContent = o.getAttribute('data-money') || priceEl.textContent;
            render();
          });
          /* Keep a click on the picker from toggling the card behind it. */
          sel.addEventListener('click', function (e) { e.stopPropagation(); });
        }
      })(cards[k]);
    }

    /* ── Sticky bar ──────────────────────────────────────────────────────── */
    if (bar && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          bar.classList.toggle('is-in', entries[i].isIntersecting);
        }
      }, { threshold: 0.12 }).observe(root);
    } else if (bar) {
      bar.classList.add('is-in');
    }

    /* ── Add to cart ─────────────────────────────────────────────────────── */
    if (submit) {
      submit.setAttribute('data-label-default', label ? label.textContent : 'Add bundle to cart');
      submit.addEventListener('click', function () {
        if (inflight || !buyable || !heroId) return;
        if (selected.length < min) return;
        inflight = true;
        submit.classList.add('is-busy');
        submit.disabled = true;
        if (label) label.textContent = 'Adding…';

        /* One shared id per click, so every line of one bundle purchase is
           identifiable in reporting without a metafield.

           The underscore prefix makes these PRIVATE line item properties, which
           Shopify documents as "available in the Liquid line_item.properties
           object and Ajax API" — i.e. private means "themes are expected to
           filter it", NOT "the platform hides it". Dawn and every theme derived
           from it drop keys beginning with `_` when rendering cart lines, which
           is why this is the convention; a theme that prints properties without
           that check would show them. Private CART ATTRIBUTES (`__`) are the
           ones the platform genuinely withholds, and they are per-cart, not
           per-line, so they cannot carry this. */
        var gid = 'bb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
        var items = [{
          id: heroId,
          quantity: 1,
          properties: { _bundle_id: gid, _bundle_role: 'hero' }
        }];
        for (var i = 0; i < selected.length; i++) {
          var vid = variantOf(selected[i]);
          if (!vid) continue;
          items.push({
            id: vid,
            quantity: 1,
            properties: { _bundle_id: gid, _bundle_role: 'addon' }
          });
        }

        var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        /* NO `sections` param on the add: an invalid one returns 400 for the
           whole request while the add still succeeds, so a caller treating
           non-2xx as failure skips every refresh step on an item that is now in
           the cart (product-shelf:404 shipped exactly that). The shared engine
           asks for sections on the /cart.js read instead. */
        fetch(root_url + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            /* Opts out of the bundle block's in-flight fetch shim. */
            'X-Modulo-Internal': '1'
          },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          /* fetch does not reject on 422. Without this gate the shopper is told
             the bundle was added when it was not. */
          if (!r.ok) throw new Error('cart/add ' + r.status);
          return r.json();
        }).then(function () {
          if (window.ModuloCart && window.ModuloCart.after) return window.ModuloCart.after();
          /* Fail OPEN: a successful add with no engine to tell the theme is
             strictly worse than landing on a correct cart page. */
          window.location.href = root_url + 'cart';
        }).catch(function () {
          if (label) label.textContent = 'Could not add — try again';
        }).then(function () {
          inflight = false;
          submit.classList.remove('is-busy');
          render();
        });
      });
    }

    root.moduloBb = true;
    render();
  }

  function bootstrap() {
    var els = document.querySelectorAll('[data-modulo-bb]');
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
  /* Theme editor re-render. */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-bb]') : null;
    if (el) initSection(el);
  });
})();
